/**
 * Pingaches (snippets/chz-pingache-dialog.liquid, chz-pingache.liquid y los de cada sección en
 * chz-section-style.liquid).
 * - Todos los Pingaches de la web son iguales: al tocarlos sueltan una frase al azar (sin repetir la
 *   anterior), la de su sección si tiene (Diseño de la sección > Frases) o las generales.
 * - El descuento sale con el 3.º, el 5.º y el 7.º Pingache distinto que la persona encuentra, en
 *   cualquier página: cada uno se anota por su id en localStorage ('chz-pingache-seen'), así el avance
 *   sigue aunque cambie de página o vuelva otro día. Tocar otra vez uno ya encontrado no suma. Si cerró
 *   el diálogo sin dejar el correo, tiene otra oportunidad en el siguiente número.
 * - Con el correo guardado, el código queda guardado en localStorage ('chz-pingache-code') y el Pingache
 *   escondido lo muestra de nuevo. Es un solo código para todos; Shopify lo deja usar una vez por cliente
 *   y solo a quien no ha comprado.
 * - A quien ya compró y entró a su cuenta (data-used en el host) no se le ofrece: los Pingaches solo hablan.
 * - Los de los costados, después de hablar, se esconden un rato.
 * - El diálogo: la X, Escape o tocar fuera del panel lo cierran. El formulario es el nativo de clientes de
 *   Shopify, que recarga la página: antes de enviarlo se anota en sessionStorage; al volver, si Shopify
 *   confirma el envío (o trae un error), el diálogo se abre solo con el resultado.
 * Sin almacenamiento disponible todo funciona igual, solo que no se recuerda.
 */
const FOUND_KEY = 'chz-pingache-code';
const SUBMIT_KEY = 'chz-pingache-submit';
const SEEN_KEY = 'chz-pingache-seen';
const PRIZE_AT = [3, 5, 7];
const TALK_MS = 3800;
const HIDE_MS = 7000;

/** @param {'localStorage' | 'sessionStorage'} store @param {string} key */
function read(store, key) {
  try {
    return window[store].getItem(key);
  } catch {
    return null;
  }
}

/** @param {'localStorage' | 'sessionStorage'} store @param {string} key @param {string} value */
function write(store, key, value) {
  try {
    window[store].setItem(key, value);
  } catch {
    /* sin almacenamiento */
  }
}

/** @param {'localStorage' | 'sessionStorage'} store @param {string} key */
function forget(store, key) {
  try {
    window[store].removeItem(key);
  } catch {
    /* sin almacenamiento */
  }
}

const host = document.querySelector('[data-chz-pingache-host]');
const code = host instanceof HTMLElement ? host.dataset.code || '' : '';
const dialog = host?.querySelector('dialog');
const hasDialog = dialog instanceof HTMLDialogElement;
/** Ya compró con la sesión iniciada: el descuento no es para esta persona. */
const used = host instanceof HTMLElement && host.dataset.used === 'true';

/** @type {string[]} */
let phrases = [];
try {
  const node = host?.querySelector('script[data-chz-pingache-phrases]');
  phrases = JSON.parse(node?.textContent || '[]').filter((phrase) => typeof phrase === 'string' && phrase.trim());
} catch {
  phrases = [];
}

let lastPhrase = '';
/** Respaldo en memoria si no hay localStorage. */
let memorySeen = new Set();

const found = () => Boolean(code) && read('localStorage', FOUND_KEY) === code;

/** Pingaches distintos que ya encontró esta persona (en cualquier página). @returns {Set<string>} */
function seen() {
  try {
    const list = JSON.parse(read('localStorage', SEEN_KEY) || '[]');
    if (Array.isArray(list)) return new Set([...memorySeen, ...list.filter((id) => typeof id === 'string')]);
  } catch {
    /* dato roto: se vuelve a empezar */
  }
  return new Set(memorySeen);
}

/** @param {string} id @returns {{ count: number, isNew: boolean }} */
function markSeen(id) {
  const ids = seen();
  const isNew = Boolean(id) && !ids.has(id);
  if (isNew) ids.add(id);
  memorySeen = ids;
  write('localStorage', SEEN_KEY, JSON.stringify([...ids]));
  return { count: ids.size, isNew };
}

/** Frases propias del Pingache (Diseño de la sección) o las generales. @param {HTMLElement} talker */
function phrasesFor(talker) {
  const node = talker.querySelector('script[data-chz-pingache-own]');
  if (!node) return phrases;
  try {
    const own = JSON.parse(node.textContent || '[]').filter((phrase) => typeof phrase === 'string' && phrase.trim());
    return own.length ? own : phrases;
  } catch {
    return phrases;
  }
}

/* ---------- Diálogo ---------- */
const formStep = host?.querySelector('[data-chz-pingache-step="form"]');
const codeStep = host?.querySelector('[data-chz-pingache-step="code"]');
const form = host?.querySelector('form');

function showCode() {
  if (formStep instanceof HTMLElement) formStep.hidden = true;
  if (codeStep instanceof HTMLElement) {
    codeStep.hidden = false;
    const title = codeStep.querySelector('[id]');
    if (title && hasDialog) dialog.setAttribute('aria-labelledby', title.id);
  }
}

function openDialog() {
  if (!hasDialog) return;
  if (found()) showCode();
  if (!dialog.open) dialog.showModal();
}

if (hasDialog) {
  for (const button of dialog.querySelectorAll('[data-chz-pingache-close]')) {
    button.addEventListener('click', () => dialog.close());
  }

  // El panel ocupa todo el diálogo: un clic cuyo destino es el propio diálogo cae fuera del panel.
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });

  form?.addEventListener('submit', () => {
    write('sessionStorage', SUBMIT_KEY, form.id);
    form.querySelector('[type="submit"]')?.setAttribute('aria-busy', 'true');
  });

  const status = dialog.querySelector('[data-chz-pingache-status]');
  dialog.querySelector('[data-chz-pingache-copy]')?.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(code);
      if (status) status.textContent = host?.getAttribute('data-label-copied') || '';
    } catch {
      const value = dialog.querySelector('[data-chz-pingache-code]');
      const selection = window.getSelection();
      if (value && selection) {
        const range = document.createRange();
        range.selectNodeContents(value);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      if (status) status.textContent = host?.getAttribute('data-label-copy-fallback') || '';
    }
  });

  // Vuelta del formulario nativo: solo cuenta si el envío salió de este formulario.
  const result = dialog.querySelector('[data-chz-pingache-result]')?.getAttribute('data-chz-pingache-result') || '';
  const submitted = Boolean(form) && (read('sessionStorage', SUBMIT_KEY) === form?.id || location.hash === `#${form?.id}`);
  if (submitted && (result === 'posted' || result === 'failed')) {
    forget('sessionStorage', SUBMIT_KEY);
    if (result === 'posted' && code) {
      write('localStorage', FOUND_KEY, code);
      showCode();
    }
    if (location.hash) history.replaceState(history.state, '', location.pathname + location.search);
    openDialog();
    if (result === 'failed') dialog.querySelector('input[type="email"]')?.focus();
  }
}

/* ---------- Pingaches que hablan ---------- */
/** @type {WeakMap<HTMLElement, number>} */
const timers = new WeakMap();

/** @param {HTMLElement} talker */
function say(talker) {
  const bubble = talker.querySelector('[data-chz-pingache-bubble]');
  const list = phrasesFor(talker);
  if (!(bubble instanceof HTMLElement) || !list.length) return;
  const pool = list.length > 1 ? list.filter((phrase) => phrase !== lastPhrase) : list;
  const phrase = pool[Math.floor(Math.random() * pool.length)] || list[0];
  lastPhrase = phrase;
  bubble.textContent = phrase;
  talker.classList.add('is-talking');
  talker.classList.remove('is-ducked');

  window.clearTimeout(timers.get(talker));
  timers.set(
    talker,
    window.setTimeout(() => {
      talker.classList.remove('is-talking');
      if (talker.dataset.chzPingacheTalker !== 'decoy') return;
      talker.classList.add('is-ducked');
      timers.set(
        talker,
        window.setTimeout(() => {
          talker.classList.remove('is-ducked');
          bubble.textContent = '';
        }, HIDE_MS)
      );
    }, TALK_MS)
  );
}

/** @param {HTMLElement} talker */
function quiet(talker) {
  window.clearTimeout(timers.get(talker));
  talker.classList.remove('is-talking');
}

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-chz-pingache-button]') : null;
  const talker = button?.closest('[data-chz-pingache-talker]');
  if (!(talker instanceof HTMLElement)) return;

  if (used || !hasDialog) {
    say(talker);
    return;
  }

  // El 3.º, 5.º y 7.º Pingache distinto dan el premio; uno ya encontrado no suma.
  const { count, isNew } = markSeen(talker.dataset.chzPingacheId || '');
  if (isNew && PRIZE_AT.includes(count)) {
    quiet(talker);
    openDialog();
    return;
  }
  // Con el código ya ganado, el escondido lo vuelve a mostrar.
  if (found() && talker.dataset.chzPingacheTalker === 'hidden') {
    quiet(talker);
    openDialog();
    return;
  }
  say(talker);
});
